// Company-admin API → backend /company router (the Admin / company-owner cockpit).
import { api } from "./client";

// Overview
export const getCompanyOverview = () => api.get("/company/overview");

// Team & per-tester extension tokens
// includeInactive also returns deactivated members — needed by any page that
// offers a Reactivate button, since a deactivated member otherwise drops out of
// the very list that holds it.
export const listTeam = ({ includeInactive = false } = {}) =>
  api.get("/company/team", { params: { include_inactive: includeInactive || undefined } });
export const inviteTester = (data) => api.post("/company/team", data);

// Developers — same endpoints, scoped by role. Developers review reports and mark
// fixes; they don't run tests, so they don't consume a tester seat.
export const listDevelopers = () => api.get("/company/team", { params: { role: "developer" } });
export const listTesters = () => api.get("/company/team", { params: { role: "tester" } });
export const inviteDeveloper = (data) => api.post("/company/team", { ...data, role: "developer" });
// A project manager plans the work: creates projects and staffs them. Only an
// Admin may appoint one — a PM inviting another PM is rejected by the server.
export const listProjectManagers = () => api.get("/company/team", { params: { role: "project_manager" } });
export const inviteProjectManager = (data) =>
  api.post("/company/team", { ...data, role: "project_manager" });
// Invite anyone the CALLER is allowed to invite; the server enforces the matrix.
export const inviteMember = ({ role, ...data }) => api.post("/company/team", { ...data, role });
export const resendInvite = (id) => api.post(`/company/team/${id}/resend-invite`, {});

// Agent tokens for one member, BOTH runners. The extension token is one per
// person; exe tokens are one per machine, so they come back as a list.
export const listMemberTokens = (id) => api.get(`/company/team/${id}/tokens`);
export const issueExeToken = (id, name) =>
  api.post(`/company/team/${id}/tokens/exe`, { name: name || null });
export const revokeMemberToken = (id, tokenId) =>
  api.del(`/company/team/${id}/tokens/${tokenId}`);
export const updateTester = (id, data) => api.put(`/company/team/${id}`, data);
// Removing someone revokes their access and their tokens, so the server asks the
// CALLER to re-enter their own password. A 403 back means the password was wrong.
export const removeTester = (id, password) =>
  api.del(`/company/team/${id}`, { password });
export const generateToken = (id) => api.post(`/company/team/${id}/token`, {});
export const revokeToken = (id) => api.del(`/company/team/${id}/token`);

// Runs & bugs (company-wide — pass projectId to scope to one project)
export const listCompanyRuns = (limit = 50, projectId) => api.get("/company/runs", { params: { limit, project_id: projectId || undefined } });
export const listCompanyBugs = (limit = 50, projectId) => api.get("/company/bugs", { params: { limit, project_id: projectId || undefined } });

// Analytics
// `days` scopes the aggregate to the last N days so the per-tester table
// matches the range selector on the page. Omitted = all time.
// `flow` scopes the aggregate to one runner. Without it the per-tester breakdown
// summed all three however the page was filtered.
export const getCompanyAnalytics = (days, flow) =>
  api.get("/company/analytics", {
    params: { days: days || undefined, flow: flow && flow !== "all" ? flow : undefined },
  });

// Activity log — superseded by GET /audit/ (api/audit.js), which scopes by the
// caller's ROLE and pages in SQL. This one is admin-only, capped at 500 rows with
// no offset, and returns no total; kept only so an older client isn't broken.
// New callers should use getAuditLog.
export const getActivityLog = (params) => api.get("/company/activity-log", { params });

// Settings
export const getCompanySettings = () => api.get("/company/settings");
export const updateCompanySettings = (data) => api.put("/company/settings", data);

// Usage report by email (weekly / monthly)
// `frequency` previews a window WITHOUT saving it — the backend owns which
// windows exist (see reporting/usage_report.FREQUENCIES).
export const getUsageReport = (frequency) =>
  api.get("/company/usage-report" + (frequency ? `?frequency=${encodeURIComponent(frequency)}` : ""));
export const updateUsageReport = (data) => api.put("/company/usage-report", data);
export const sendUsageReport = () => api.post("/company/usage-report/send", {});

// Company API keys (CI/bots)
export const listCompanyKeys = () => api.get("/company/api-keys");
export const createCompanyKey = (name) => api.post("/company/api-keys", { name });
export const deleteCompanyKey = (id) => api.del(`/company/api-keys/${id}`);
